import { useEffect, useRef } from 'react';
import { useNotificationStore } from '../store/notificationStore';
import type { UserNotification } from '../types/notification';
import { notificationService } from '../services/notificationService';
import { useToast } from '../components/common/Toast';
import { useLanguage } from '../features/language/useLanguage';
import { isForbiddenError } from '../services/api';

const BACKOFFS_MS = [1000, 2000, 4000, 8000, 16000];
const JITTER_MS = 250;
// EventSource has no access to HTTP status codes on error, so we can't tell a
// 403 from a network blip. Cap retries so an un-permissioned user doesn't
// silently hammer the backend every 16s forever.
const MAX_STREAM_ATTEMPTS = 5;

function streamUrl(token: string): string {
  // Mirror the axios baseURL convention so EventSource goes directly to the backend
  // (Vite has no /api/v1 proxy, and relative URLs would hit the dev server).
  const base = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';
  return `${base}/notifications/stream?token=${encodeURIComponent(token)}`;
}

export function useNotificationStream(token: string | null) {
  const esRef = useRef<EventSource | null>(null);
  const attemptRef = useRef(0);
  const mountedRef = useRef(true);

  const pushLive = useNotificationStore((s) => s.pushLive);
  const applyReplay = useNotificationStore((s) => s.applyReplay);
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);

  const { showToast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    mountedRef.current = true;
    if (!token) return;

    let forbidden = false;

    const connect = () => {
      if (!mountedRef.current || forbidden) return;
      if (attemptRef.current >= MAX_STREAM_ATTEMPTS) return;
      const es = new EventSource(streamUrl(token));
      esRef.current = es;

      es.addEventListener('connected', () => {
        attemptRef.current = 0;
      });

      es.addEventListener('replay', (e) => {
        try {
          const n = JSON.parse((e as MessageEvent).data) as UserNotification;
          applyReplay([n]);
        } catch {
          // ignore malformed
        }
      });

      es.addEventListener('notification', (e) => {
        try {
          const n = JSON.parse((e as MessageEvent).data) as UserNotification;
          pushLive(n);
          showToast(t('notifications.toast.newIncoming', undefined, 'New notification'), 'info');
        } catch {
          // ignore malformed
        }
      });

      es.onerror = () => {
        es.close();
        esRef.current = null;
        if (!mountedRef.current || forbidden) return;
        attemptRef.current += 1;
        if (attemptRef.current >= MAX_STREAM_ATTEMPTS) return;
        const idx = Math.min(attemptRef.current, BACKOFFS_MS.length - 1);
        const delay = BACKOFFS_MS[idx] + Math.random() * JITTER_MS;
        setTimeout(connect, delay);
      };
    };

    // Seed unread count once at mount. Use the outcome as a probe: if it 403s,
    // the user has no access to notifications, so don't bother opening the stream.
    notificationService
      .getUnreadCount()
      .then((r) => {
        setUnreadCount(r.count);
        connect();
      })
      .catch((err) => {
        if (isForbiddenError(err)) {
          forbidden = true;
          return;
        }
        connect();
      });

    return () => {
      mountedRef.current = false;
      esRef.current?.close();
      esRef.current = null;
    };
  }, [token, pushLive, applyReplay, setUnreadCount, showToast, t]);
}
